import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CURRENCIES } from "@/lib/constants";
import { toast } from "sonner";
import { Megaphone, Sparkles, ArrowRight } from "lucide-react";

const CREATOR_TYPES = ["Influencer", "UGC Agency", "Performance Marketing Agency"];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const needsRole = !user?.role;
  const [role, setRole] = useState(user?.role || null);
  const [accountType, setAccountType] = useState(user?.role === "influencer" ? user?.account_type || null : null);
  const [currency, setCurrency] = useState(user?.currency || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [saving, setSaving] = useState(false);

  const isInfluencer = role === "influencer";
  const roleOk = role && (role !== "influencer" || accountType);
  const canSubmit = roleOk && currency && bio.trim().length > 3;

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Please complete all fields to continue.");
      return;
    }
    setSaving(true);
    try {
      const payload = { currency, bio };
      if (needsRole) {
        payload.role = role;
        payload.account_type = role === "influencer" ? accountType : "Brand";
      }
      const res = await api.put("/profile", payload);
      setUser(res.data);
      toast.success("Great! Now complete your profile.");
      navigate("/profile");
    } catch (e) {
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const RoleIcon = isInfluencer ? Sparkles : Megaphone;

  const roleCard = (key, Icon, title, desc) => {
    const active = role === key;
    return (
      <button type="button" key={key} onClick={() => { setRole(key); if (key !== "influencer") setAccountType(null); }}
        data-testid={`onboarding-role-${key}`}
        className={`text-left rounded-2xl border p-4 transition-colors ${active ? "border-brand-pink bg-brand-pink/10 ring-2 ring-brand-pink" : "border-border bg-background hover:bg-surfaceHover"}`}>
        <Icon className={`h-6 w-6 mb-2 ${active ? "text-brand-pink" : "text-muted-foreground"}`} />
        <p className="font-heading font-bold text-sm">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center p-5">
      <div className="grain" />
      <div className="glow-orb bg-neon-pink" style={{ width: 380, height: 380, top: -60, right: -40 }} />
      <div className="glow-orb bg-neon-purple" style={{ width: 340, height: 340, bottom: -80, left: -60 }} />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 w-full max-w-xl rounded-3xl border border-border bg-surface p-8 sm:p-10">
        <div className="flex justify-center mb-6">
          <img src="/logo-square.png" alt="InfluencerCollab" className="h-24 w-24 object-contain rounded-2xl bg-white p-1" />
        </div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon-lime text-center mb-2">Almost there</p>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-center mb-3">Welcome to InfluencerCollab</h1>

        {needsRole ? (
          <div className="mb-6">
            <Label className="mb-2 block text-center">How will you use InfluencerCollab?</Label>
            <div className="grid grid-cols-2 gap-3">
              {roleCard("influencer", Sparkles, "Creator", "Find campaigns & bid")}
              {roleCard("promoter", Megaphone, "Brand / Promoter", "Post campaigns & hire")}
            </div>
            {role === "influencer" && (
              <div className="mt-3">
                <Label className="mb-2 block text-xs text-muted-foreground">What type of creator are you?</Label>
                <div className="flex flex-wrap gap-2 justify-center">
                  {CREATOR_TYPES.map((t) => {
                    const active = accountType === t;
                    return (
                      <button type="button" key={t} onClick={() => setAccountType(t)}
                        data-testid={`onboarding-accounttype-${t.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-brand-pink text-white border-brand-pink font-semibold" : "border-border bg-background text-muted-foreground hover:text-foreground"}`}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 mb-8">
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full border border-brand-pink/30 bg-brand-pink/10 text-brand-pink font-semibold">
              <RoleIcon className="h-4 w-4" />
              {isInfluencer ? user?.account_type || "Creator" : "Brand / Promoter"}
            </span>
          </div>
        )}

        <div className="space-y-5">
          <div>
            <Label className="mb-2 block">Preferred currency</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="currency-select" className="bg-background border-border h-11">
                <SelectValue placeholder="Select your currency" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`currency-${c.code}`}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-2 block">{isInfluencer ? "Short bio about you & your content" : "Tell us about your brand / business"}</Label>
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              data-testid="bio-input"
              placeholder={isInfluencer ? "e.g. Fashion & lifestyle creator based in Mumbai…" : "e.g. D2C skincare brand looking for authentic creators…"}
              className="bg-background border-border min-h-[90px]"
            />
          </div>
        </div>

        <Button onClick={submit} disabled={!canSubmit || saving} data-testid="onboarding-submit-btn" className="w-full mt-8 h-12 rounded-full bg-neon-lime text-background hover:bg-neon-lime/90 font-bold text-base">
          {saving ? "Saving…" : "Continue to my profile"} <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </motion.div>
    </div>
  );
}
